import { queriesEngagements, queriesWorksites } from "@query";
import {
  WSEmptyState,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import React, { useMemo } from "react";

import { TableWorksites } from "./components/TableWorksites/TableWorksites";
import { useWorksiteActions } from "./hooks/useWorksiteActions";
import { selectorWorksiteTableItems } from "./selectors/selectorWorksiteTableItems";

export const Worksites: React.FC = () => {
  // Work sites are a bounded, account-level settings collection and the whole
  // list is needed anyway — to join engagements per row and to know whether a
  // primary already exists. Hence `useQueryAll` rather than paging.
  const queryWorksites = queriesWorksites.listWorksites.useQueryAll();
  const queryEngagements = queriesEngagements.listEngagements.useQueryAll();

  const items = useMemo(
    () =>
      selectorWorksiteTableItems({
        worksites: queryWorksites.flatData ?? [],
        engagements: queryEngagements.flatData ?? []
      }),
    [queryWorksites.flatData, queryEngagements.flatData]
  );

  const actions = useWorksiteActions({
    isFirstWorksite: (queryWorksites.flatData ?? []).length === 0
  });

  return (
    <WSPage>
      <WSPageToolbar
        title="Work site"
        actions={[
          {
            label: "Create work site",
            buttonKind: "Primary",
            icon: "plus",
            onClick: actions.create
          }
        ]}
      />

      <WSList gap="2XL">
        {queryWorksites.isError ? (
          <WSEmptyState
            type="error"
            title="Couldn't load work sites"
            description="Something went wrong loading your work sites. Please try again."
            orientation="horizontal"
            actions={{
              buttons: [
                { label: "Try again", onClick: () => queryWorksites.refetch() }
              ]
            }}
          />
        ) : !queryWorksites.isLoading && items.length === 0 ? (
          <WSEmptyState
            type="card"
            title="No work sites yet"
            description="W-2 reporting needs a work site for every engagement that isn't remote. Create one to get started."
            orientation="horizontal"
            actions={{
              buttons: [{ label: "Create work site", onClick: actions.create }]
            }}
          />
        ) : (
          <TableWorksites
            items={items}
            loading={queryWorksites.isLoading}
            onEdit={actions.edit}
            onDelete={actions.remove}
          />
        )}
      </WSList>
    </WSPage>
  );
};
